import { useCallback, useRef } from 'react';
import { useAnalyticsEvents, fireTrackAnalytics } from '@atlassian/jira-product-analytics-bridge';

import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { fireErrorAnalytics, fireSiteOptedOutAnalytics } from '../../services/analytics/index.tsx';
import { useCheckLoomCrossJoinNudgeEligibility } from '../../services/jira-loom/eligibility.tsx';
import type { EligibilityCheck } from './types.tsx';

export const useFireLoomCjnFeatureExposedEventOnce = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const eventIsFiredRef = useRef(false);

	return useCallback(
		({ cohort, flagKey }: { cohort: string; flagKey: string }) => {
			if (eventIsFiredRef.current) {
				return;
			}
			if (cohort === 'not-enrolled') {
				return;
			}
			fireTrackAnalytics(createAnalyticsEvent({}), 'crossJoinNudgesJiraToLoomFeature exposed', {
				flagKey,
				cohort,
			});
			eventIsFiredRef.current = true;
		},
		[createAnalyticsEvent],
	);
};
export const useLoomCrossJoinNudgeEligibility = () => {
	const cloudId = useCloudId();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const checkLoomCrossJoinNudgeEligibility = useCheckLoomCrossJoinNudgeEligibility();

	return useCallback(async (): Promise<EligibilityCheck> => {
		try {
			const eligibilityResponse = await checkLoomCrossJoinNudgeEligibility();
			if (!eligibilityResponse.eligible) {
				if (eligibilityResponse.ineligibilityReason === 'SITE_OPTED_OUT') {
					fireSiteOptedOutAnalytics(createAnalyticsEvent, 'crossJoinNudgesJiraToLoom');
				}
				return { isEligible: false, ineligibleReason: eligibilityResponse.ineligibilityReason };
			}
			return { isEligible: true, data: {} };

			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			fireErrorAnalytics('crossJoinNudgesJiraToLoom', error, {
				cloudId,
				jiraToLoomCrossJoinNudgesErrorType: 'eligibilityCheckFailed',
			});
			return { isEligible: false, ineligibleReason: 'unexpected error' };
		}
	}, [cloudId, createAnalyticsEvent, checkLoomCrossJoinNudgeEligibility]);
};
